import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  AlertTitle,
  useTheme,
  LinearProgress,
  Button,
  Chip
} from '@mui/material';
import NatureIcon from '@mui/icons-material/Nature';
import DiversityIcon from '@mui/icons-material/Diversity3';
import WarningIcon from '@mui/icons-material/Warning';
import PaidIcon from '@mui/icons-material/Paid';

// Future component imports
// import ESGSpendMap from '../../components/dashboard/ESGSpendMap';
// import DiverseSupplierChart from '../../components/dashboard/DiverseSupplierChart';
// import CarbonFootprintChart from '../../components/dashboard/CarbonFootprintChart';
// import ESGRiskMatrix from '../../components/dashboard/ESGRiskMatrix';

interface ESGCategory {
  name: string;
  spend: number;
  carbonIntensity: number; // CO2e per $1000 spent
  carbonFootprint: number; // Total CO2e
  hasSustainableAlternatives: boolean;
}

interface DiverseSupplier {
  id: string;
  name: string;
  category: string;
  spend: number;
  diversityType: string[]; // e.g., "Minority-owned", "Women-owned", etc.
  region: string;
}

interface SupplierESGRisk {
  id: string;
  name: string;
  category: string;
  spend: number;
  riskLevel: 'Low' | 'Medium' | 'High';
  riskReasons: string[];
  remediationStatus: 'Not Started' | 'In Progress' | 'Completed';
}

const SustainabilityPage: React.FC = () => {
  const theme = useTheme();

  // Mock data for demonstration
  const [esgCategories, setEsgCategories] = useState<ESGCategory[]>([
    {
      name: 'Logistics & Transportation',
      spend: 5400000,
      carbonIntensity: 48.2,
      carbonFootprint: 260280,
      hasSustainableAlternatives: true
    },
    {
      name: 'Energy & Utilities',
      spend: 3200000,
      carbonIntensity: 75.4,
      carbonFootprint: 241280,
      hasSustainableAlternatives: true
    },
    {
      name: 'Office Supplies',
      spend: 1500000,
      carbonIntensity: 12.7,
      carbonFootprint: 19050,
      hasSustainableAlternatives: true
    },
    {
      name: 'IT Equipment',
      spend: 4800000,
      carbonIntensity: 22.5,
      carbonFootprint: 108000,
      hasSustainableAlternatives: true
    },
    {
      name: 'Facility Management',
      spend: 3700000,
      carbonIntensity: 18.9,
      carbonFootprint: 69930,
      hasSustainableAlternatives: true
    },
    {
      name: 'Raw Materials',
      spend: 8900000,
      carbonIntensity: 32.8,
      carbonFootprint: 291920,
      hasSustainableAlternatives: false
    }
  ]);

  const [diverseSuppliers, setDiverseSuppliers] = useState<DiverseSupplier[]>([
    {
      id: 'DS001',
      name: 'GreenTech Solutions',
      category: 'IT Services',
      spend: 1800000,
      diversityType: ['Minority-owned'],
      region: 'North America'
    },
    {
      id: 'DS002',
      name: 'Eco Office Supplies',
      category: 'Office Supplies',
      spend: 750000,
      diversityType: ['Women-owned', 'Small Business'],
      region: 'EMEA'
    },
    {
      id: 'DS003',
      name: 'Global Inclusive Logistics',
      category: 'Logistics & Transportation',
      spend: 1200000,
      diversityType: ['Veteran-owned'],
      region: 'North America'
    },
    {
      id: 'DS004',
      name: 'Sustainable Building Services',
      category: 'Facility Management',
      spend: 980000,
      diversityType: ['Small Business', 'Minority-owned'],
      region: 'APAC'
    }
  ]);

  const [supplierRisks, setSupplierRisks] = useState<SupplierESGRisk[]>([
    {
      id: 'SR001',
      name: 'Global Manufacturing Inc',
      category: 'Raw Materials',
      spend: 3200000,
      riskLevel: 'High',
      riskReasons: ['Labor standards violations', 'Environmental compliance issues'],
      remediationStatus: 'In Progress'
    },
    {
      id: 'SR002',
      name: 'IndustrialChem Corp',
      category: 'Raw Materials',
      spend: 2400000,
      riskLevel: 'Medium',
      riskReasons: ['Environmental compliance issues'],
      remediationStatus: 'In Progress'
    },
    {
      id: 'SR003',
      name: 'FastShip Logistics',
      category: 'Logistics & Transportation',
      spend: 1800000,
      riskLevel: 'Medium',
      riskReasons: ['Carbon emissions concerns', 'Labor standards issues'],
      remediationStatus: 'Not Started'
    }
  ]);

  // Business Rules Implementation
  const diverseSuppliersTarget = 5; // Target percentage for diverse supplier spend
  const totalSpend = esgCategories.reduce((sum, cat) => sum + cat.spend, 0);
  const diverseSupplierSpend = diverseSuppliers.reduce((sum, supplier) => sum + supplier.spend, 0);
  const diverseSupplierPercentage = (diverseSupplierSpend / totalSpend) * 100;
  
  const highCarbonCategories = esgCategories
    .sort((a, b) => b.carbonFootprint - a.carbonFootprint)
    .slice(0, 3);

  const highRiskSuppliers = supplierRisks.filter(supplier => supplier.riskLevel === 'High');

  // Alerts based on business rules
  const diverseSupplierAlert = diverseSupplierPercentage < diverseSuppliersTarget && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>Diverse Supplier Spend Below Target</AlertTitle>
      Current diverse supplier spend is {diverseSupplierPercentage.toFixed(1)}% of total spend,
      which is below the corporate target of {diverseSuppliersTarget}%.
      Consider expanding engagement with qualified diverse suppliers.
    </Alert>
  );

  const carbonFootprintAlert = highCarbonCategories.length > 0 && (
    <Alert severity="info" sx={{ mt: 2 }}>
      <AlertTitle>High Carbon Footprint Categories</AlertTitle>
      Top 3 categories by carbon footprint:
      <ul style={{ marginTop: '8px', marginBottom: '0px' }}>
        {highCarbonCategories.map((category, index) => (
          <li key={index}>
            {category.name}: {(category.carbonFootprint / 1000).toFixed(1)} tons CO2e 
            {category.hasSustainableAlternatives ? ' (Sustainable alternatives available)' : ''}
          </li>
        ))}
      </ul>
    </Alert>
  );

  const esgRiskAlert = highRiskSuppliers.length > 0 && (
    <Alert severity="error" sx={{ mt: 2 }}>
      <AlertTitle>ESG High Risk Suppliers Identified</AlertTitle>
      {highRiskSuppliers.length} suppliers flagged for significant ESG or compliance issues:
      <ul style={{ marginTop: '8px', marginBottom: '0px' }}>
        {highRiskSuppliers.map((supplier, index) => (
          <li key={index}>
            {supplier.name} ({supplier.category}): {supplier.riskReasons.join(', ')}. 
            Remediation status: {supplier.remediationStatus}.
          </li>
        ))}
      </ul>
    </Alert>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Sustainability & ESG Insights
      </Typography>

      <Grid container spacing={3}>
        {/* ESG Spend Map */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Global ESG Spend Map
              {/* <ESGSpendMap /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* ESG KPI Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Diverse Supplier Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                backgroundColor: diverseSupplierPercentage >= diverseSuppliersTarget 
                  ? 'rgba(76, 175, 80, 0.1)' 
                  : 'rgba(255, 152, 0, 0.1)'
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <DiversityIcon 
                      color={diverseSupplierPercentage >= diverseSuppliersTarget ? 'success' : 'warning'} 
                      sx={{ mr: 1, fontSize: 28 }}
                    />
                    <Typography variant="h6">Diverse Suppliers</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {diverseSupplierPercentage.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of total spend ({diverseSuppliersTarget}% target)
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(diverseSupplierPercentage / diverseSuppliersTarget * 100, 100)}
                      color={diverseSupplierPercentage >= diverseSuppliersTarget ? 'success' : 'warning'}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Carbon Footprint Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <NatureIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6">Carbon Footprint</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {(esgCategories.reduce((sum, cat) => sum + cat.carbonFootprint, 0) / 1000).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    tons CO2e from supplier spend
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Chip 
                      label={`${highCarbonCategories.length} High Impact Categories`} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* ESG Risk Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                backgroundColor: highRiskSuppliers.length > 0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)'
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <WarningIcon 
                      color={highRiskSuppliers.length > 0 ? 'error' : 'success'} 
                      sx={{ mr: 1, fontSize: 28 }} 
                    />
                    <Typography variant="h6">ESG Risk</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {highRiskSuppliers.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    suppliers with high ESG risk
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Chip 
                      label={
                        highRiskSuppliers.length === 0 
                          ? 'No High Risks' 
                          : 'Remediation Required'
                      } 
                      size="small" 
                      color={highRiskSuppliers.length > 0 ? 'error' : 'success'}
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* ESG Investment Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PaidIcon color="secondary" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6">ESG Investment</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    ${(diverseSupplierSpend / 1000000).toFixed(1)}M
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    spent with diverse suppliers
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Button variant="outlined" color="secondary" size="small">
                      View Opportunities
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Diverse Supplier Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Diverse Supplier Spend Distribution
              {/* <DiverseSupplierChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Carbon Footprint Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Carbon Footprint by Category
              {/* <CarbonFootprintChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Sustainability Insights */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sustainability Insights
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {diverseSupplierAlert}
              {carbonFootprintAlert}
              {esgRiskAlert}
            </CardContent>
          </Card>
        </Grid>

        {/* ESG Risk Matrix */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Supplier ESG Risk Matrix
              {/* <ESGRiskMatrix /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SustainabilityPage; 